import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Event, Router, NavigationEnd } from '@angular/router';
import { AuthService } from './auth/services/auth.service';
import { ComandService } from './tech-support/services/comand.service';
import { ReceiverStatusComponent } from './shared/components/receiver-status/receiver-status.component';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { PanelStatusService } from './map/service/panel-status.service';
import { ReceiversService } from './shared/services/receivers.service';
import { PanelsService } from './shared/services/panels.service';
import { PartitionsService } from './shared/services/partitions.service';
import { UsersService } from './shared/services/users.service';
import { CompaniesService } from 'src/app/settings/services/companies.service';
import { UsersService as UsersDataService } from 'src/app/shared/services/users.service';
import { CustomMapService } from './map/service/custom-map.service';
import { EventService } from './panel/services/event.service';
import { LeftSideMenuNavigation } from './panel/models/event.model';
import { HoverMenuesService } from './hover-menues/services/hover-menues.service';
import { ProfileService } from './profile/services/profile.service';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SignalSearchIntervalService } from 'src/app/signal-strength/services/signal-search-interval.service';
import constants from './shared/constants';
import { AdminCompanyPickerComponent } from './shared/components/admin-company-picker/admin-company-picker';
import { roles } from 'src/constants/roles';
import { CompanyUserService } from './settings/services/company-user.service';
import { WebppSocketService } from './shared/services/webppSocket.service';
import { PartitionListComponent } from './partitions/components/partition-list/partition-list.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('helpButton', { static: false }) helpButton!: ElementRef;
  @ViewChild('receiverSelector', { static: false }) receiverSelector!: ElementRef;

  title = 'mialarma-users-ui';
  companyLogo: string;
  showMenu = false;
  showEnvironmentalWarning = false;
  environment = environment.env;
  dialogRef: MatDialogRef<AdminCompanyPickerComponent>;

  private routerSubscription: Subscription;
  @ViewChild(PartitionListComponent) partitionListComponent: PartitionListComponent;

  helpBubble = {
    icon: 'info',
    title: 'AppComponent.HelpMessage_title',
    description: 'AppComponent.HelpMessage_description',
    confirm: 'AppComponent.HelpMessage_confirm',
    onConfirm: () => {
      this.authService.updateMessageCheck('helpMenuMessage');
    },
    onClose: () => {
      this.hoverMenuesService.helpBubbleMessageStatus = false;
    },
  };

  constructor(
    private readonly loadingBar: LoadingBarService,
    public router: Router,
    public authService: AuthService,
    public commandService: ComandService,
    private readonly dialog: MatDialog,
    public translateService: TranslateService,
    public readonly panelStatus: PanelStatusService,
    public receiversService: ReceiversService,
    private readonly panelsService: PanelsService,
    private readonly partitionsService: PartitionsService,
    private readonly usersDataService: UsersDataService,
    private readonly usersService: UsersService,
    private readonly companiesService: CompaniesService,
    private readonly customMapService: CustomMapService,
    private readonly eventService: EventService,
    private readonly signalSearchIntervalService: SignalSearchIntervalService,
    public hoverMenuesService: HoverMenuesService,
    public companyUserService: CompanyUserService,
    public profileService: ProfileService,
    public wsSocket: WebppSocketService
  ) {
    this.router.events.subscribe((event: Event) => {
      this.translateService.setDefaultLang(this.authService.selectedLanguage);
      this.translateService.use(this.authService.selectedLanguage);
    });

    if (this.authService.isLoggedIn()) {
      this.receiversService.connectReceivers();
    }

    this.eventService.menuBarNavigation.subscribe((signalParams: LeftSideMenuNavigation) => {
      if (signalParams.closeLeftMenuNavigationBar && this.showMenu) this.toggleShowMenu();
    });

    this.eventService.adminChooseCompanyEvent.subscribe((signalParams: { openPopup: boolean }) => {
      this.handleCompanySwitch(signalParams.openPopup);
    });

    this.authService.openBetaWarningModal();
  }

  handleCompanySwitch(openPopup: boolean) {
    if (!openPopup) return;

    this.promptOnlyAdminUsers();
  }

  ngOnInit(): void {
    if (environment.env !== 'Production') {
      this.routerSubscription = this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this.showEnvironmentalWarning = true;
        });
    }
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  getMessageWarning = () => {
    if (this.environment === 'Local' || this.environment === 'Testing') {
      return this.translateService.instant('AppComponent.EnvironmentalWarningTesting');
    }
    if (this.environment === 'Staging') {
      return this.translateService.instant('AppComponent.EnvironmentalWarningStaging');
    }
  };

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: any): void {
    if (this.authService.hasRoleAdmin()) {
      if (this.companyUserService.adminHasChosenCompany()) {
        event.preventDefault();
        event.returnValue = '';
      }
    }

    if (this.authService.user) {
      localStorage.setItem('last-navigation-path', this.router.url);
    }

    // Sin esto el socket abandonado sigue en las salas del apiKey hasta que el servidor lo expira
    // (~45s), y el backend le sigue enviando listas completas mientras la pestaña nueva ya pidió
    // las suyas. Es el coste que paga toda la empresa por cada F5.
    this.teardownSockets();
  }

  @HostListener('window:load', ['$event'])
  onAfterLoad(event: any): void {
    if (this.authService.hasRoleAdmin()) {
      if (this.companyUserService.adminHasChosenCompany()) {
        this.adminClearSpecificsForCompanySelector();
        this.refreshResourcesOfApp();
      }
    }
  }

  async openAdminCompanyPickerModal(): Promise<{ companyId: string }> {
    const companyId = localStorage.getItem(constants.companyIdChosenByAdmin);

    if (companyId) return Promise.resolve({ companyId });

    if (this.dialogRef?.getState() === MatDialogState.OPEN) {
      return;
    }

    return new Promise<{ companyId: string }>((resolve, reject) => {
      this.dialogRef = this.dialog.open(AdminCompanyPickerComponent, {
        autoFocus: true,
        disableClose: true,
        backdropClass: 'backdrop',
      });

      this.dialogRef.afterClosed().subscribe((result) => {
        if (result?.companyId) {
          resolve({ companyId: result.companyId });
        } else {
          reject();
        }
      });
    });
  }

  private async promptOnlyAdminUsers() {
    if (!this.authService.isLoggedIn()) return;

    if (this.authService.user.role !== roles.ADMIN) return;

    const companyId = localStorage.getItem(constants.companyIdChosenByAdmin);

    if (companyId) {
      return;
    }

    this.router.navigateByUrl('/maps');

    const res = await this.openAdminCompanyPickerModal();

    if (res?.companyId) {
      localStorage.setItem(constants.companyIdChosenByAdmin, res.companyId);
    }

    await this.refreshResourcesOfApp();
  }

  async refreshResourcesOfApp() {
    if (this.receiversService.receivers.length === 0) {
      await this.receiversService.getReceivers();
    } else {
      await this.receiversService.leaveReceiversApiKeysChannels();
      await this.receiversService.joinReceiversApiKeysChannels();
    }

    this.adminClearSpecificsForCompanySelector();

    await this.panelsService.getPanels(true, localStorage.getItem(constants.companyIdChosenByAdmin));
    this.usersDataService.requestUsers();
    this.partitionListComponent?.getPartitions();
  }

  logout = async () => {
    // stop() en vez de complete(): en un cierre de sesión hay que forzar el contador a cero, no
    // restarle uno, porque cualquier carga en curso queda abandonada.
    this.loadingBar.useRef().stop();
    // Antes de authService.logout(), que en producción hace un window.location.href y deja sin
    // ejecutar todo lo que venga después.
    // El servidor cierra los sockets de este usuario (otras pestañas, otros equipos) antes de que
    // se pierda el token, que es lo único que lo identifica.
    await this.authService.closeServerSession();
    this.teardownSockets();
    this.authService.logout();
    this.panelStatus.closeMapSession(this.router.url == '/maps');
    this.commandService.cleanCommandData();
    this.commandService.cleanFilters();
    this.commandService.clearMessageArray();
    this.customMapService.setDefaultZoom();
    this.customMapService.clear();

    this.signalSearchIntervalService.clear();
    this.usersService.clear();
    this.panelsService.clear();
    this.partitionsService.clear();
    this.receiversService.clear();
    this.usersDataService.clear();
    this.profileService.clear();
    this.companiesService.clear();
    this.closeProfileMenu();
  };

  /** Deja las salas y cierra los sockets para no dejar conexiones zombis en el servidor. */
  private teardownSockets() {
    this.receiversService.leaveReceiversApiKeysChannels();
    this.wsSocket.close();
  }

  adminClearSpecificsForCompanySelector = () => {
    this.panelStatus.clearMap();
    this.usersService.clear();
    this.panelsService.clear();
    this.partitionsService.clear();
  };

  onReceiverStatusClick = () => {
    this.dialog.open(ReceiverStatusComponent, {
      width: '45%',
      maxWidth: '1000px',
    });
  };

  toggleShowMenu(): void {
    this.showMenu = !this.showMenu;
  }

  resetMapZoomAndCleanRestorationFocusSystem() {
    this.customMapService.setDefaultZoom();
    this.customMapService.cleanRestorationOfFocus();
    this.panelStatus.resetMapSelection();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.toggleShowMenu();
    }
  }

  onToggleHelpMenu() {
    this.hoverMenuesService.onToggleHelpMenu();
  }

  onToggleProfileMenu() {
    this.hoverMenuesService.onToggleProfileMenu();
  }

  closeProfileMenu() {
    this.hoverMenuesService.profileMenuStatus = false;
  }
}
